import { BasicModal } from "@repo/components/src/shared/modal";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Badge, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Text } from "@repo/components/src/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "@repo/components/src/ui/tooltip";
import { VStack } from "@repo/components/src/ui/vstack";
import { Download, ExternalLink } from "lucide-react-native";
import React from "react";
import { Linking, ScrollView } from "react-native";
import { SentimentBar } from "../../organic/commentParts";
import DeliverableVideo from "../../organic/deliverableVideo";
import { formatDateLong, platformLabel } from "../utils";
import CommentsAnalyzer from "./commentsAnalyzer";
import { PostDetailView } from "./types";

type Props = {
  view: PostDetailView;
};

// Shared core rendered by every post-detail entry point: the campaign
// report's deliverable gallery (via deliverableModal's thin adapter), the
// consolidated report's content gallery, and brand posts. Layout preserved
// from the original organic-only DeliverableModal; the only addition is the
// embedded comments analyzer, gated by `view.commentsFilter`.
const PostDetailModal = React.memo(({ view }: Props) => {
  const publishedAt = formatDateLong(view.publishedAt);
  const isSelfReported = !!view.selfReported;
  const selfReportedExpired = view.selfReported?.expired ?? false;

  const goToPost = () => {
    if (view.postUrl) Linking.openURL(view.postUrl);
  };

  const downloadVideo = () => {
    const url = view.contentUrl;
    if (!url) return;
    if (typeof document !== "undefined") {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener";
      link.download = "";
      link.click();
      return;
    }
    Linking.openURL(url);
  };

  const downloadButton = (
    <Button
      action="brand"
      variant="outline"
      onPress={downloadVideo}
      isDisabled={!view.contentUrl}
      className="w-full bg-background-0"
    >
      <ButtonIcon as={Download} />
      <ButtonText>Descargar video</ButtonText>
    </Button>
  );

  const goButton = (
    <Button
      action="brand"
      variant="solid"
      onPress={goToPost}
      isDisabled={!view.postUrl || selfReportedExpired}
      className="w-full"
    >
      <ButtonIcon as={ExternalLink} />
      <ButtonText>Ver publicación</ButtonText>
    </Button>
  );

  return (
    <BasicModal
      title={
        <HStack space="md" className="items-center">
          <Avatar size="md">
            <AvatarFallbackText>{view.author.name}</AvatarFallbackText>
            <AvatarImage source={{ uri: view.author.avatarUrl ?? undefined }} />
          </Avatar>
          <VStack className="flex-1">
            <HStack space="sm" className="items-center flex-wrap">
              <Heading size="md">{view.author.name}</Heading>
              {view.campaignName ? (
                <Badge action="muted" rounded="full" size="sm">
                  <BadgeText>{view.campaignName}</BadgeText>
                </Badge>
              ) : null}
            </HStack>
            <Text size="xs" className="text-typography-500">
              {platformLabel(view.platformCode)} · {view.postName}
              {publishedAt ? ` · publicado ${publishedAt}` : ""}
            </Text>
          </VStack>
        </HStack>
      }
      modalBodyProps={{
        scrollEnabled: false,
        className: "flex-1 overflow-hidden",
        contentContainerClassName: "flex-1",
      }}
    >
      <HStack space="lg" className="items-start flex-1 h-full">
        {/* Left: 9:16 preview + actions.

            The actions are pinned under the video rather than sharing a
            scroll area with it: the preview takes the column's full height,
            so anything below it fell off the bottom and "Descargar video"
            was effectively invisible. */}
        <VStack space="sm" className="w-80 shrink-0 h-full">
          {/* The preview takes the height left over after the actions, rather
              than deriving it from 9:16 — at this column's width that box is
              taller than the modal body, which pushed "Descargar video" out of
              sight. */}
          <Box className="w-full flex-1 min-h-0">
            <DeliverableVideo
              contentUrl={view.contentUrl}
              thumbnailUrl={view.thumbnailUrl}
              alt={view.postName}
              fill
            />
          </Box>
          {/* No permalink can ever exist (e.g. screenshot-reported), so the
              button would only ever render disabled. */}
          {!view.hasPermalink ? null : selfReportedExpired ? (
            <Tooltip
              placement="top"
              trigger={(triggerProps) => (
                <Box {...triggerProps} className="w-full">
                  {goButton}
                </Box>
              )}
            >
              <TooltipContent>
                <TooltipText>
                  Las historias expiran después de 24 horas.
                </TooltipText>
              </TooltipContent>
            </Tooltip>
          ) : (
            goButton
          )}
          {/* For a self-reported deliverable the download sits in the
              right-hand column: the left one is only as wide as the 9:16
              video and the label wraps. */}
          {isSelfReported ? null : downloadButton}
        </VStack>

        {/* Right: stats → sentiment → comments */}
        {isSelfReported ? (
          // Self-reported deliverables have no comments to analyse, so the
          // column is just the numbers — one per row, spread down the
          // video's full height, with the download action pinned underneath.
          <VStack className="flex-1 shrink h-full" space="md">
            <VStack className="flex-1 justify-between">
              {view.stats.map((stat) => (
                <HStack
                  key={stat.label}
                  className="bg-background-50 border border-outline-100 rounded-lg px-4 py-3 items-center justify-between"
                >
                  <Text size="sm" className="text-typography-500 uppercase">
                    {stat.label}
                  </Text>
                  <Text size="xl" className="font-bold text-typography-900">
                    {stat.value}
                  </Text>
                </HStack>
              ))}
            </VStack>
            {downloadButton}
          </VStack>
        ) : (
          <Box className="flex-1 shrink h-full">
            <ScrollView
              contentContainerClassName="gap-3"
              className="flex-1 h-full"
              showsVerticalScrollIndicator={false}
            >
              <HStack className="flex-wrap -m-1">
                {view.stats.map((stat) => (
                  <Box key={stat.label} className="w-1/3 p-1">
                    <Box className="bg-background-50 border border-outline-100 rounded-lg px-3 py-2">
                      <Text
                        size="2xs"
                        className="text-typography-500 uppercase"
                      >
                        {stat.label}
                      </Text>
                      <Text size="md" className="font-bold text-typography-900">
                        {stat.value}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </HStack>

              {view.sentiment && view.sentiment.totalCount > 0 ? (
                <VStack space="sm">
                  <HStack
                    space="md"
                    className="justify-between items-baseline flex-wrap"
                  >
                    <Text
                      size="xs"
                      className="text-typography-700 font-semibold uppercase"
                    >
                      Sentimiento del post
                    </Text>
                    <Text size="xs" className="text-typography-500">
                      {view.sentiment.distribution.analyzed} de{" "}
                      {view.sentiment.totalCount} comentarios analizados
                    </Text>
                  </HStack>
                  <SentimentBar distribution={view.sentiment.distribution} />
                </VStack>
              ) : null}

              {view.commentsFilter ? (
                <CommentsAnalyzer filter={view.commentsFilter} />
              ) : null}
            </ScrollView>
          </Box>
        )}
      </HStack>
    </BasicModal>
  );
});

PostDetailModal.displayName = "PostDetailModal";

export default PostDetailModal;
